import { LessonlyIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/lessonly';
import { CF } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  Switch,
  TextField
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DataFieldWithLabel } from '../../../../../../components/DataFieldWithLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../../components/InfoBox';
import { useTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { useFeatureEnabled } from '../../../../../../services/features';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  MASK,
  masked
} from '../common';
import {
  getLessonlyWebhookUrl,
  removeLessonlyConfig,
  useLessonlyConfig
} from './service';

const NAME = 'Lessonly';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const SetupDialog = ({
  config,
  open,
  onClose,
  onSuccessfulIntegration
}: IntegrationSetupDialogWithConfigProperties<LessonlyIntegrationConfig>) => {
  const { t } = useTranslation();

  const {
    subdomain,
    apiKey,
    webhookActive,
    webhookAuthToken,
    orgId
  } = config?.data || { subdomain: '', apiKey: '' };

  const [state, setState] = useState<{
    subdomain: string;
    apiKey: string;
  }>({
    subdomain,
    apiKey: apiKey ? MASK : ''
  });
  useEffect(() => {
    setState({ subdomain, apiKey: masked(apiKey) });
  }, [subdomain, apiKey]);

  const completionTrackingEnabled = useFeatureEnabled(
    'LESSONLY_COMPLETION_TRACKING_V1'
  );

  if (!config) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('org.integrations.lessonly.setupInfo.p1')}
        </Alert>

        <FormRow>
          <CustomInputLabel
            label={t('org.integrations.common.subdomain')}
            fullWidth
          >
            <TextField
              variant="outlined"
              value={state.subdomain}
              onChange={(ev) =>
                setState((s) => ({ ...s, subdomain: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
        <FormRow>
          <CustomInputLabel
            label={t('org.integrations.common.apiKey')}
            fullWidth
          >
            <TextField
              variant="outlined"
              value={state.apiKey}
              onChange={(ev) =>
                setState((s) => ({ ...s, apiKey: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>

        {completionTrackingEnabled && (
          <>
            {subdomain && apiKey && (
              <FormControlLabel
                label={
                  webhookActive
                    ? t(
                        'org.integrations.lessonly.setupInfo.webhook.deactivate'
                      )
                    : t('org.integrations.lessonly.setupInfo.webhook.activate')
                }
                control={
                  <Switch
                    checked={webhookActive}
                    onChange={() => {
                      callFirebaseFunction(CF.lessonly.activateWebhook, {
                        orgId,
                        active: !webhookActive
                      });
                    }}
                    color="primary"
                  />
                }
              />
            )}
            {webhookActive && (
              <>
                <div>
                  <InfoBox>
                    {t(
                      'org.integrations.lessonly.setupInfo.webhook.instructions',
                      {
                        subdomain
                      }
                    )}
                  </InfoBox>
                </div>
                <div>
                  <DataFieldWithLabel
                    label={'Webhook URL'}
                    copyToClipboardText={getLessonlyWebhookUrl()}
                  >
                    {getLessonlyWebhookUrl()}
                  </DataFieldWithLabel>
                </div>
                <div>
                  <DataFieldWithLabel
                    label={'Authorization Header'}
                    copyToClipboardText={webhookAuthToken}
                  >
                    {webhookAuthToken}
                  </DataFieldWithLabel>
                </div>
              </>
            )}
          </>
        )}
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeLessonlyConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
            disabled={!subdomain && !apiKey}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={() =>
                callFirebaseFunction(CF.lessonly.verifyCredentialsAndStore, {
                  orgId: config.data.orgId,
                  subdomain: state.subdomain,
                  apiKey: state.apiKey
                }).then(onSuccessfulIntegration)
              }
              disabled={
                !state.subdomain ||
                !state.apiKey ||
                state.subdomain === config.data.subdomain ||
                state.apiKey === config.data.apiKey
              }
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

export const LessonlySettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useLessonlyConfig(org.id, userId);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/lessonly.png"
      active={!!cfg?.data.active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          config={cfg}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
